/*
 * Apparel "Size Guide" modal behavior (snippets/new-pdp-size-guide.liquid).
 *
 * Tabs carry data-size-guide-tab / data-size-guide-panel; a tab only exists
 * when its metafield has content. IN/CM toggle: values are authored in inches;
 * CM rewrites every number in [data-size-guide-convert] cells (×2.54,
 * rounded), with the inch original kept in data-in for switching back.
 */
if (!customElements.get('new-pdp-size-guide')) {
  let uid = 0;
  customElements.define(
    'new-pdp-size-guide',
    class NewPdpSizeGuide extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-size-guide-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-size-guide-panel]'));
        this.unitButtons = Array.from(this.querySelectorAll('[data-unit]'));

        const base = `new-pdp-size-guide-${(uid += 1)}`;
        this.tabs.forEach((tab, index) => {
          const panel = this.panels.find(
            (candidate) => candidate.dataset.sizeGuidePanel === tab.dataset.sizeGuideTab
          );
          const tabId = `${base}-tab-${index}`;
          tab.id = tabId;
          // Roving tabindex — only the selected tab is a tab stop
          tab.setAttribute('tabindex', tab.classList.contains('is-active') ? '0' : '-1');
          if (panel) {
            const panelId = panel.id || `${base}-panel-${index}`;
            panel.id = panelId;
            panel.setAttribute('aria-labelledby', tabId);
            tab.setAttribute('aria-controls', panelId);
          }
          tab.addEventListener('click', () => this.activateTab(tab.dataset.sizeGuideTab));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        this.unitButtons.forEach((button) =>
          button.addEventListener('click', () => this.setUnit(button.dataset.unit))
        );

        // WebKit drops the list role under list-style:none; the <ol> is
        // metafield output, so it can't carry the attribute in Liquid.
        this.querySelectorAll('.new-pdp-size-guide__measure-content ol').forEach((ol) =>
          ol.setAttribute('role', 'list')
        );
      }

      onKeydown(event, index) {
        const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 }[event.key];
        let target;
        if (event.key === 'Home') {
          target = this.tabs[0];
        } else if (event.key === 'End') {
          target = this.tabs[this.tabs.length - 1];
        } else if (step) {
          target = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
        } else {
          return;
        }
        event.preventDefault();
        this.activateTab(target.dataset.sizeGuideTab);
        target.focus();
      }

      activateTab(name) {
        this.tabs.forEach((tab) => {
          const active = tab.dataset.sizeGuideTab === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.sizeGuidePanel !== name;
        });
        this.retrapModalFocus();
      }

      // Dawn's trapFocus snapshots first/last at open, so toggling panels can
      // strand them in a hidden panel and let Tab escape. Re-run it in place.
      retrapModalFocus() {
        if (typeof trapFocus !== 'function') return;
        const modal = this.closest('modal-dialog');
        if (!modal || !modal.hasAttribute('open')) return;
        const keep = this.contains(document.activeElement)
          ? document.activeElement
          : modal.querySelector('[role="dialog"]');
        trapFocus(modal, keep);
      }

      setUnit(unit) {
        this.unitButtons.forEach((button) => {
          const active = button.dataset.unit === unit;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.querySelectorAll('[data-size-guide-convert]').forEach((cell) => {
          const inches = cell.dataset.in || '';
          cell.textContent =
            unit === 'cm'
              ? inches.replace(/\d+(\.\d+)?/g, (n) => Math.round(parseFloat(n) * 2.54))
              : inches;
        });
      }
    }
  );
}